/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { mapCollectionsForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import useDiscoveryCollectionsData from 'bundles/front-page/hooks/useDiscoveryCollectionsData';
import type { ImageType } from 'bundles/unified-common/components/Image';
import CollectionsModule from 'bundles/unified-common/modules/CollectionsModule';
import type { Filter } from 'bundles/unified-common/modules/CollectionsModule';

type Props = {
  headline: string;
  description: string;
  ctaText: string;
  ctaTarget: string;
  showCtaArrowIcon?: boolean;
  collectionSets: {
    id: string;
    context: string;
    contextId: string;
    filterName?: string;
  }[];
  numberOfEntriesPerCollection?: number;
  backgroundImage?: ImageType;
  layout: string;
};

const styles = {
  container: css`
    margin: var(--cds-spacing-400) 0;
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
  `,
};

const SpotlightCollections = ({
  headline,
  description,
  ctaText,
  ctaTarget,
  showCtaArrowIcon,
  collectionSets,
  numberOfEntriesPerCollection,
  backgroundImage,
  layout,
}: Props) => {
  const [currentCollectionSetIndex, setCurrentCollectionSetIndex] = React.useState(0);
  const handleFilterChange = (index: number) => {
    setCurrentCollectionSetIndex(index);
  };

  const filters = collectionSets.map((collectionSet, index) => {
    return {
      id: index.toString(),
      filterName: collectionSet.filterName,
    } as Filter;
  });

  const { collections, loadingCollections: isCollectionsLoading } = useDiscoveryCollectionsData({
    contextId: collectionSets[currentCollectionSetIndex].contextId as string,
    contextType: collectionSets[currentCollectionSetIndex].context as string,
  });

  // remove collection title
  const formattedCollections = mapCollectionsForCarousel(collections).map((collection) => {
    return {
      ...collection,
      title: '',
    };
  });

  return (
    <div css={styles.container}>
      <CollectionsModule
        filters={filters.length > 0 ? filters : null}
        onFilterChange={handleFilterChange}
        currentCollectionSetIndex={currentCollectionSetIndex}
        collections={formattedCollections}
        numOfEntriesPerCollection={numberOfEntriesPerCollection || 3}
        isCollectionsLoading={isCollectionsLoading}
        showAllEntries
        lazyLoadImage
        description={description}
        headline={headline}
        layout={layout}
        backgroundColor="light-blue"
        showRoundedCorners
        backgroundImage={backgroundImage}
        cta={{
          target: ctaTarget,
          text: ctaText,
          variant: 'primary',
          showCtaArrowIcon,
        }}
      />
    </div>
  );
};

export default SpotlightCollections;
